import { particleOperatorName, supportedParticleOperators } from './particle-simulation.js';

// Only fields consumed by the shared simulator are editable. Labels preserve
// Valve names so duplicate authored components remain distinguishable.
export const particleFields = {
    system: ['max_particles', 'initial_particles', 'radius', 'color', 'rotation', 'rotation_speed'],
    emit_continuously: ['emission_rate', 'emission_start_time', 'emission_duration'],
    emit_instantaneously: ['num_to_emit', 'emission_start_time'],
    'emit noise': ['emission minimum', 'emission maximum', 'emission_start_time', 'emission_duration'],
    'lifetime random': ['lifetime_min', 'lifetime_max'],
    'radius random': ['radius_min', 'radius_max'],
    'color random': ['color1', 'color2'],
    'alpha random': ['alpha_min', 'alpha_max'],
    'velocity random': ['random_speed_min', 'random_speed_max', 'speed_in_local_coordinate_system_min', 'speed_in_local_coordinate_system_max'],
    'position within sphere random': ['distance_min', 'distance_max', 'speed_min', 'speed_max'],
    'position within box random': ['min', 'max'],
    'movement basic': ['gravity', 'drag'],
};
export const particleGroups = ['emitters', 'initializers', 'operators', 'forces', 'constraints', 'renderers', 'children'];
export function inspectionComponents(library) {
    return library.definitions.flatMap((definition, d) => [
        { key: `${d}:system`, label: `${definition.name} · System`, value: definition, fields: particleFields.system, supported: true },
        ...particleGroups.flatMap(group => definition[group].map((value, index) => {
            const name = group === 'children' ? 'child' : particleOperatorName(value);
            return { key: `${d}:${group}:${index}`, label: `${definition.name} · ${group} ${index + 1} · ${value.name}`, value,
                fields: particleFields[name] ?? [], supported: group === 'children' || supportedParticleOperators.has(name) };
        })),
    ]);
}
export function overrideLibrary(original, overrides = {}, disabled = new Set()) {
    const result = structuredClone(original);
    for (const component of inspectionComponents(result)) {
        for (const field of component.fields) {
            const value = overrides[component.key]?.[field];
            if (value === undefined) continue;
            const values = Array.isArray(value) ? value : [value];
            if (!values.length || values.length > 4 || !values.every(Number.isFinite)) throw new Error(`Invalid ${field}`);
            component.value[field] = Array.isArray(value) ? [...value] : value;
        }
    }
    result.definitions.forEach((definition, d) => {
        for (const group of particleGroups) definition[group] = definition[group].filter((_, i) => !disabled.has(`${d}:${group}:${i}`));
    });
    return result;
}
export function folderEntries(assets, folder = '', query = '', kind = '') {
    const prefix = folder ? folder + '/' : '', results = new Map();
    for (const asset of assets) {
        if (!asset.path.startsWith(prefix) || kind && asset.kind !== kind || !asset.path.toLowerCase().includes(query.toLowerCase())) continue;
        const slash = asset.path.indexOf('/', prefix.length);
        const entry = !query && slash >= 0 ? { path: asset.path.slice(0, slash), kind: 'folder', size: 0 } : asset;
        results.set(entry.path, entry);
    }
    return [...results.values()].sort((a, b) => Number(b.kind === 'folder') - Number(a.kind === 'folder') || (a.path < b.path ? -1 : a.path > b.path ? 1 : 0));
}
export class PreviewClock {
    time = 0; speed = 1; paused = false;
    tick(dt, hidden = false) { if (!this.paused && !hidden) this.time += Math.max(0, Math.min(dt, .05)) * 1000 * this.speed; return this.time; }
    restart() { this.time = 0; }
    step(fps = 30) { this.paused = true; this.time += 1000 / Math.max(1, fps); }
}
